import { cn } from '@/lib/cn';
import { ColorMintPopover } from '@/components/builder/color-mint';
import { OpenThemeTokenLink, ThemeTokenSample } from '@/components/builder/token-navigation';
import type { Theme } from '@personalizehq/schemas/ir';
import * as CollapsiblePrimitive from '@radix-ui/react-collapsible';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import * as SliderPrimitive from '@radix-ui/react-slider';
import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { ChevronDown, ChevronRight, ChevronUp, Info, TriangleAlert, type LucideIcon } from 'lucide-react';
import { Children, isValidElement, useEffect, useMemo, useRef, useState, type ReactElement } from 'react';

// The control vocabulary. Radix now, native before — and the reason for the switch is not taste:
// a native `<select>` CANNOT put an icon in an option, so the panel's enums could never say what they
// meant (`fit: fill` sits three rows from a size `fill` and means something else entirely), and
// `<details>` cannot animate, so its header degenerated into a `›` nobody read as a control.
//
// THE LAW THIS FILE STILL OBEYS, and it is the reason the migration was worth planning rather than
// just doing. `inspector.test.tsx` walks every control BY ARIA ROLE and asserts the document still
// publishes. Radix keeps every one of those roles — that is precisely why it is the right dependency:
//
//   select        → Radix Select      (role="combobox" + role="option" — the options live in the
//                   CONTENT, not on the element, so the walker OPENS it and reads them)
//   toggle group  → Radix ToggleGroup (role="radio" in role="radiogroup", type="single") + roving focus
//   slider        → Radix Slider      (role="slider", aria-valuemin/max — the schema's bounds survive)
//   switch        → Radix Switch      (role="switch" — CHANGED from checkbox; the walker knows)
//   number        → native <input type="number">  (role="spinbutton") — STAYS NATIVE. The SCRUB IS ON
//                   THE LABEL precisely so the input underneath survives untouched.
//   popover       → a real <button> trigger, marked [data-popover] so the walker can open it
//   section       → a real <button> trigger, marked [data-section]  (was: details.open)
//   disclosure    → a real <button>, marked [data-more]
//
// A control the test cannot reach by role is a control a screen-reader user cannot reach either.
// That is not a tax on the design; it is the same rule as A4.

/** THE ONE CONTROL SURFACE, so every widget in the panel is the same object. Tokens only — a raw
 *  colour here is a control that breaks in the other theme, and the builder is dark-first.
 *
 *  It said "the one" while there were THREE copies: this, a near-identical one in
 *  `inspector-fields.tsx` differing only by the `disabled:` clauses, and a third that grew in the
 *  font-variant popover. Three copies of "the one control surface" is how a panel drifts a pixel at a
 *  time — so it is exported now, and a new control that needs a different surface has to say why. */
export const CONTROL =
  'h-8 min-w-0 rounded-lg border border-transparent bg-muted/70 px-2 text-[12px] transition-colors ' +
  'hover:bg-muted focus-visible:border-ring focus-visible:bg-background focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring ' +
  'disabled:cursor-not-allowed disabled:opacity-50';

// ---------------------------------------------------------------------------- section

/** A Radix `Collapsible`, not a `<details>`. The native element cannot animate its own open state
 *  (there is no height to transition to), and its `<summary>` marker is a browser-drawn triangle you
 *  fight rather than style — which is why the old header shrank to a bare `›` nobody read as a
 *  control.
 *
 *  `hasIssue` FORCES it open: there is zero section→field mapping to drift, and an error can never
 *  hide behind a closed header. That was true of the `<details>` and it stays true here — it is the
 *  one behaviour in this file worth more than the animation.
 *
 *  THE WALKER OPENS THESE BY CLICKING `[data-section]`. It used to reach in and set `details.open`,
 *  which no longer exists; the trigger is a real button, which is what a screen-reader user has too. */
export function Section({
  title,
  hasIssue,
  quiet,
  defaultOpen,
  children,
}: {
  title: string;
  hasIssue?: boolean;
  /** NOTHING ON THIS NODE IS SET HERE, so the section opens as a bare header row.
   *
   *  This is `Disclosure` raised one level: that hides unset ROWS, this hides an unset SECTION. It is
   *  what pays for removing the design/settings tabs — `motion`, `interaction` and `compat` are unset
   *  on very nearly every node, so three sections cost three 32px rows instead of three open bodies,
   *  and the sections that ARE carrying values are the ones you can see.
   *
   *  It is a DEFAULT, not a lock: the header is still a control, and `hasIssue` still overrides it. */
  quiet?: boolean | undefined;
  defaultOpen?: boolean | undefined;
  children: React.ReactNode;
}): React.JSX.Element {
  const [open, setOpen] = useState(defaultOpen ?? !quiet);
  const shown = open || Boolean(hasIssue);

  return (
    <CollapsiblePrimitive.Root open={shown} onOpenChange={setOpen} className="border-b border-border/60">
      {/* STICKY. With the tabs gone a block shows up to seven sections in one column, and a header
          that scrolls away takes with it the only answer to "which section am I editing". The
          background must be OPAQUE for the same reason — a translucent hover would let the rows
          underneath show through as it passes. */}
      <CollapsiblePrimitive.Trigger
        data-section=""
        className="group sticky top-0 z-10 grid h-8 w-full grid-cols-[minmax(0,1fr)_26px] items-center bg-background px-3 text-left text-[11px] font-semibold text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
      >
        <span className="truncate">{title}</span>
        {/* THE RAIL — 26px, reserved on every header whether or not it has anything to put there, so
            the panel's right-hand affordances sit on one continuous gutter instead of floating after
            whatever width the last control happened to be. The two occupants are mutually exclusive
            by construction: `hasIssue` forces `shown`, so the chevron can never coexist with it. */}
        <span className="flex items-center justify-end">
          {hasIssue ? (
            <TriangleAlert className="size-3.5 text-destructive" />
          ) : !shown ? (
            <ChevronRight className="size-3.5 text-muted-foreground" />
          ) : null}
        </span>
      </CollapsiblePrimitive.Trigger>

      <CollapsiblePrimitive.Content className="overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
        <div className="space-y-1.5 px-3 pb-3 pt-0">{children}</div>
      </CollapsiblePrimitive.Content>
    </CollapsiblePrimitive.Root>
  );
}

// ---------------------------------------------------------------------------- hint

/** WHY IS THIS GREYED OUT. The audit's highest-value panel finding: `aspect_ratio` under an explicit
 *  height and `justify` on a hugging main axis can be set and do NOTHING — and the panel had no way
 *  to say which OTHER field neutered them.
 *
 *  A disabled control with no explanation is worse than a live one that lies: the author cannot even
 *  guess what to change. So a disabled row keeps its value, greys, and grows this. */
export function Hint({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <TooltipPrimitive.Provider delayDuration={200}>
      <TooltipPrimitive.Root>
        <TooltipPrimitive.Trigger asChild>
          <button
            type="button"
            // NOT `aria-label`: this button is not a control, and naming it would put a phantom row
            // into the inventory golden. It is described BY its content, for the screen reader, and
            // the tooltip is the same string.
            className="text-muted-foreground/60 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            <Info className="size-3" />
            <span className="sr-only">{children}</span>
          </button>
        </TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content
            side="left"
            sideOffset={6}
            className="z-50 max-w-[220px] rounded-md border bg-popover px-2 py-1.5 text-[11px] leading-relaxed text-popover-foreground shadow-md"
          >
            {children}
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
}

// ---------------------------------------------------------------------------- disclosure

/** PROGRESSIVE DISCLOSURE — the actual answer to "a stack shows ~25 rows".
 *
 *  A row whose value is not set does not appear at all; it waits behind a pill. `max_width` and
 *  `min_width` simply do not exist on screen until you have used them. This is what takes a `stack`
 *  from ~25 rows to about 8, and it is the single idea that makes the panel authorable.
 *
 *  It reads `set` off its children rather than taking two lists, so the call site stays a flat run
 *  of rows and no row can drift into the wrong bucket. */
export function Disclosure({ children }: { children: React.ReactNode }): React.JSX.Element | null {
  const [open, setOpen] = useState(false);
  const rows = Children.toArray(children).filter(isValidElement) as ReactElement<{ set?: boolean }>[];
  const shown = rows.filter((row) => row.props.set);
  const hidden = rows.filter((row) => !row.props.set);

  if (!rows.length) return null;
  if (rows.length === 1) return rows[0]!;
  return (
    <>
      {shown}
      {open ? hidden : null}
      {hidden.length ? (
        <button
          type="button"
          data-more=""
          aria-label={open ? 'fewer' : `${hidden.length} more`}
          title={open ? 'Show fewer settings' : `Show ${hidden.length} more settings`}
          // POSITION-INDEPENDENT, AND IT SAYS WHAT IT IS. This was a 12px tab with `-mb-4`, styled to
          // bleed into the section's bottom padding — which silently required it to be the LAST thing
          // in its section. `SizeField` now renders a placement readout after it, so the paragraph
          // was pulled up over the tab AND, being later in the DOM, painted on top of it: the pill
          // looked broken because every click landed on the paragraph.
          //
          // It also said nothing. A bare 12px chevron is not an affordance an author hunting for
          // `max width` will find, which is half of why the row read as missing. The count is the
          // label now, and it matches the accessible name rather than restating it.
          className="mx-auto mt-2 flex h-6 items-center gap-1 rounded-md px-2 text-[11px] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          onClick={() => setOpen((o) => !o)}
        >
          {open ? <ChevronUp className="size-3.5" /> : <ChevronDown className="size-3.5" />}
          {open ? 'Fewer' : `${hidden.length} more`}
        </button>
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------- toggle group

/** An enum as icons — a SEGMENTED CONTROL, which is what it always wanted to look like.
 *
 *  Radix `ToggleGroup type="single"` gives ROVING FOCUS for free: the group is one tab stop and the
 *  arrow keys move within it, which is how a segmented control is supposed to behave and is a thing
 *  the hand-rolled version simply did not do (five buttons, five tab stops, in a panel with dozens).
 *
 *  It still renders `role="radio"` inside `role="radiogroup"`, so the walker and the inventory golden
 *  are untouched — the one migration in this slice that costs the contract nothing. */
export function ToggleGroup<T extends string>({
  label,
  value,
  options,
  onChange,
  disabled,
  fill,
}: {
  label: string;
  value: T | undefined;
  options: readonly { value: T; icon: LucideIcon; title?: string }[];
  onChange: (value: T) => void;
  /** Inert here — see `Field.inert`. The control keeps its value and stops pretending to work. */
  disabled?: boolean | undefined;
  /** A primary flow selector may occupy the full property-group width. */
  fill?: boolean | undefined;
}): React.JSX.Element {
  return (
    <ToggleGroupPrimitive.Root
      type="single"
      disabled={disabled ?? false}
      // An empty string is Radix's "nothing selected", and it is also what it hands back when you
      // click the ACTIVE item (a segmented control has no "off" state — you cannot un-pick an
      // alignment). Swallow that, or every second click would clear the field.
      value={value ?? ''}
      onValueChange={(next) => next && onChange(next as T)}
      aria-label={label}
      className={cn(
        'flex gap-0.5 rounded-lg bg-muted/70 p-0.5',
        fill && 'w-full',
        disabled && 'cursor-not-allowed opacity-50'
      )}
    >
      {options.map((option) => (
        <ToggleGroupPrimitive.Item
          key={option.value}
          value={option.value}
          aria-label={`${label} ${option.value}`}
          title={option.title ?? option.value}
          className={cn(
            // size-7 inside the track's p-0.5 makes the group exactly `h-8`, so a segmented control
            // and a text field on adjacent rows sit on the same rhythm.
            'flex items-center justify-center rounded-md text-muted-foreground transition-all',
            fill ? 'h-7 min-w-0 flex-1' : 'size-7',
            'hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
            // The lift: the ACTIVE cell is a raised chip on a recessed track, not a tinted square.
            'data-[state=on]:bg-background data-[state=on]:text-foreground data-[state=on]:shadow-sm'
          )}
        >
          <option.icon className="size-3.5" />
        </ToggleGroupPrimitive.Item>
      ))}
    </ToggleGroupPrimitive.Root>
  );
}

// ---------------------------------------------------------------------------- placement grid

/** PLACEMENT AS A PICTURE, not as two enums.
 *
 *  A `box` places its children by `align_x` × `align_y`; a `stack` by `justify` (main axis) × `align`
 *  (cross axis). Both are 2-D placements, and the panel used to ask the author to reconstruct the
 *  picture from two separate 1-D icon rows — for a `stack`, from two rows whose icon sets SWAP
 *  MEANING with `direction`. One grid says it at a glance.
 *
 *  THE CALLER OWNS THE MAPPING from its fields to SCREEN axes, which is exactly where the direction
 *  flip belongs: this control knows only "left→right" and "top→bottom", so it cannot lie about an
 *  axis the way a fixed icon set can.
 *
 *  ALIGNMENT IS A CONTAINER PROPERTY. `BoxNode.align_x`/`align_y` place EVERY child, as do
 *  `StackNode.align`/`justify` — so this control only ever appears on the container. Putting a pin
 *  grid on a child would silently move its siblings. A child gets a read-only readout instead.
 *
 *  Nine `role="radio"` cells inside one `role="radiogroup"` — the same shape `ToggleGroup` already
 *  uses, so the walker drives it with no new machinery.
 *
 *  `undefined` on an axis means the field holds a value that is NOT A POSITION (`stretch`,
 *  `space_between`): no cell is lit, and the companion `AlignExtra` switch is on instead. */
export function AlignGrid<X extends string, Y extends string>({
  label,
  columns,
  rows,
  x,
  y,
  onChange,
  spacious,
  lockedAxis,
}: {
  label: string;
  /** Left → right on screen. */
  columns: readonly X[];
  /** Top → bottom on screen. */
  rows: readonly Y[];
  x: X | undefined;
  y: Y | undefined;
  /** Both axes, always — a placement is one decision. C2's "this axis is neutered by another field"
   *  is the CALLER's to enforce here, because only the caller holds the real field value: a locked
   *  `justify` may be sitting on `space_between`, which is not one of these cells and which the grid
   *  therefore cannot hand back. The `Field`'s `inert` hint names the culprit, as everywhere else. */
  onChange: (x: X, y: Y) => void;
  /** Layout panels give a spatial control enough room to read as a canvas, not a keypad. */
  spacious?: boolean | undefined;
  /** The screen axis a neutered field has frozen. Cells OFF that axis's current value are DISABLED,
   *  not merely discarded-on-click: a live cell that lights up somewhere other than where the author
   *  clicked is worse than the "live and does nothing" defect the old disabled ToggleGroup named —
   *  it is live and does something else. When the frozen axis holds a non-position (`space_between`),
   *  no cell can honour a click, so all nine lock and the `Field`'s inert fix is the way out. */
  lockedAxis?: 'x' | 'y' | undefined;
}): React.JSX.Element {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={cn(
        'inline-grid grid-cols-3 gap-px border border-border bg-muted/70',
        spacious ? 'h-[112px] min-w-0 flex-1 rounded-xl p-2' : 'w-fit shrink-0 rounded-md p-1'
      )}
    >
      {rows.map((row) =>
        columns.map((column) => {
          const on = column === x && row === y;
          const locked = lockedAxis === 'x' ? column !== x : lockedAxis === 'y' ? row !== y : false;
          return (
            <button
              key={`${String(column)}-${String(row)}`}
              type="button"
              role="radio"
              aria-checked={on}
              aria-label={`${label} ${column} ${row}`}
              title={`${column} · ${row}`}
              disabled={locked}
              onClick={() => onChange(column, row)}
              className={cn(
                'flex items-center justify-center transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-ring',
                locked ? 'cursor-default' : 'hover:bg-muted',
                spacious ? 'h-full w-full rounded-md' : 'size-[22px] rounded-[3px]'
              )}
            >
              {/* The lit cell draws the CONTENT sitting in that position; the rest are faint anchors.
                  A grid of nine identical dots would say where you can go but not what goes there. */}
              <span
                className={cn(
                  'transition-all',
                  on
                    ? spacious
                      ? 'h-4 w-1 rounded-full bg-primary'
                      : 'size-2.5 rounded-[2px] bg-primary'
                    : locked
                      ? 'size-1 rounded-full bg-muted-foreground/10'
                      : 'size-1 rounded-full bg-muted-foreground/30'
                )}
              />
            </button>
          );
        })
      )}
    </div>
  );
}

/** The values that are not a POSITION — `stretch` (span the axis) and `space_between` (distribute
 *  along it). They get no cell because they are not a place to be; they ride beside the grid.
 *
 *  Turning one OFF restores `start` rather than a remembered previous value: the author cannot see
 *  what was remembered, and `start` is what every one of these fields means when it means nothing
 *  in particular. */
export function AlignExtra({
  label,
  on,
  disabled,
  onChange,
}: {
  label: string;
  on: boolean;
  disabled?: boolean | undefined;
  onChange: (on: boolean) => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      title={label}
      disabled={disabled ?? false}
      onClick={() => onChange(!on)}
      className={cn(
        CONTROL,
        'whitespace-nowrap text-[11px] text-muted-foreground',
        on && 'border-primary bg-background text-foreground'
      )}
    >
      {label}
    </button>
  );
}

// ---------------------------------------------------------------------------- slider

/** For BOUNDED values only — `opacity` (0–1) and `parallax` (−1…1). (Unbounded extents get `useScrub`
 *  instead: a slider needs two ends, and `gap` has only one.)
 *
 *  Radix renders the thumb with `role="slider"` and the same `aria-valuemin`/`max`/`now` a native
 *  range carries, so the widget still carries the SCHEMA'S BOUNDS and still cannot author a value
 *  outside them — which was the entire reason the native one was chosen. What it adds is a track we
 *  can actually style: `accent-color` gives you a coloured thumb and nothing else. */
export function Slider({
  label,
  value,
  fallback,
  min,
  max,
  step,
  onChange,
}: {
  label: string;
  value: number | undefined;
  /** What the slider sits at while the key is ABSENT — the schema's own default, never 0. */
  fallback: number;
  min: number;
  max: number;
  step: number;
  onChange: (value: number) => void;
}): React.JSX.Element {
  return (
    <>
      {/* THE NAME LIVES ON THE THUMB, NOT THE ROOT. The thumb is what carries `role="slider"`; naming
          the root too puts a SECOND element with this label in the tree, and `getByLabelText` then
          finds two and throws. (It did.) One control, one name. */}
      <SliderPrimitive.Root
        min={min}
        max={max}
        step={step}
        value={[value ?? fallback]}
        onValueChange={([next]) => onChange(next!)}
        className="relative flex h-8 min-w-0 flex-1 touch-none select-none items-center"
      >
        <SliderPrimitive.Track className="relative h-1 w-full grow overflow-hidden rounded-full bg-muted">
          <SliderPrimitive.Range className="absolute h-full bg-indigo-500" />
        </SliderPrimitive.Track>
        <SliderPrimitive.Thumb
          aria-label={label}
          className="block size-3 rounded-full border border-indigo-500 bg-background shadow-sm transition-transform hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </SliderPrimitive.Root>
      <input
        type="number"
        aria-label={`${label} value`}
        min={min}
        max={max}
        step={step}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? fallback : Number(e.target.value))}
        className={cn(CONTROL, 'w-[56px]')}
      />
    </>
  );
}

// ---------------------------------------------------------------------------- scrub

export type ScrubProps = { onPointerDown: (e: React.PointerEvent) => void };

/** Drag the LABEL to change the number. Plasmic's `DraggableDimLabel`, and the reason it is on the
 *  label and not the input: the `<input type="number">` underneath stays a plain spinbutton, which
 *  is precisely what keeps `inspector.test.tsx` able to walk it. Shared by every unbounded extent
 *  in the panel (gap, padding, spacing), which is what makes the whole thing feel alive. */
export function useScrub(opts: {
  value: number | undefined;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
}): ScrubProps {
  // The drag reads the LATEST value/handler, but the handler itself must be stable — a scrub that
  // re-subscribed mid-drag would drop the pointer. Written in an effect, not during render.
  const latest = useRef(opts);
  useEffect(() => {
    latest.current = opts;
  });

  return useMemo(
    () => ({
      onPointerDown: (e: React.PointerEvent) => {
        const { value, min = 0, max = Number.MAX_SAFE_INTEGER, step = 1 } = latest.current;
        let acc = value ?? 0;
        let last = e.clientX;
        const target = e.currentTarget as HTMLElement;

        const move = (ev: PointerEvent): void => {
          // `movementX` is what pointer lock reports; the clientX delta is the fallback for when
          // the lock is refused (and for jsdom, which has neither).
          const dx = ev.movementX || ev.clientX - last;
          last = ev.clientX;
          acc = Math.min(max, Math.max(min, acc + dx * step));
          latest.current.onChange(Number((Math.round(acc / step) * step).toFixed(4)));
        };
        const up = (): void => {
          window.removeEventListener('pointermove', move);
          window.removeEventListener('pointerup', up);
          document.exitPointerLock?.();
        };

        target.requestPointerLock?.();
        window.addEventListener('pointermove', move);
        window.addEventListener('pointerup', up);
      },
    }),
    []
  );
}

// ---------------------------------------------------------------------------- popover

/** The trigger is a real `<button>` carrying `[data-popover]`: the walker opens every one of them
 *  before it walks, so nothing hides inside. */
export function Popover({
  label,
  trigger,
  children,
  className,
}: {
  label: string;
  trigger: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}): React.JSX.Element {
  return (
    <PopoverPrimitive.Root>
      <PopoverPrimitive.Trigger
        data-popover=""
        aria-label={label}
        className={cn(CONTROL, 'flex items-center gap-1.5 px-1.5', className)}
      >
        {trigger}
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          sideOffset={6}
          align="end"
          className="z-50 w-[212px] rounded-md border bg-popover p-2 text-popover-foreground shadow-md"
        >
          {children}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

// ---------------------------------------------------------------------------- token grid

/** A colour swatch shows BOTH halves of the pair, because that is what the token actually is. */
export function Swatch({ pair, className }: { pair?: { light: string; dark: string } | undefined; className?: string }): React.JSX.Element {
  if (!pair) return <span className={cn('size-4 rounded-sm border border-dashed', className)} />;
  return (
    <span className={cn('flex size-4 overflow-hidden rounded-sm border', className)}>
      <span className="w-1/2" style={{ background: pair.light }} />
      <span className="w-1/2" style={{ background: pair.dark }} />
    </span>
  );
}

/** NO RAW COLOUR PICKER, EVER. Every colour on the wire is a token with a light/dark pair (SPEC §4),
 *  so a hex field would silently kill dark mode on every install. Plasmic wraps Pickr because it
 *  permits raw colours; we do not, and the constraint makes the control simpler AND teaches the law.
 *
 *  The swatches are `role="radio"` — a grid of buttons is what this is, and saying so is free. */
/** THE SWATCHES, WITHOUT THE POPOVER THAT USUALLY HOSTS THEM.
 *
 *  Split out because a Radix popover CANNOT be nested inside another one: the inner layer's content
 *  is portalled to the body, so the outer treats the interaction as outside itself and dismisses.
 *  The background row edits its colour from inside its own popover, and rendering `TokenGrid` there
 *  made every swatch unreachable — invisibly to a click, and provably to the inventory walker, which
 *  opens `[data-popover]` one at a time.
 *
 *  So a popover's CONTENT is a component, and the popover is a host. Anything that needs the content
 *  in a different container asks for the content. */
export function TokenSwatchGrid({
  label,
  kind,
  theme,
  value,
  onChange,
  optional,
}: {
  label: string;
  kind: 'colors' | 'fonts' | 'buttons';
  theme: Theme;
  value: string | null | undefined;
  onChange: (value: string | undefined) => void;
  optional?: boolean | undefined;
}): React.JSX.Element {
  return (
    <div role="radiogroup" aria-label={label} className="grid grid-cols-6 gap-1">
      {optional ? (
        <button
          type="button"
          role="radio"
          aria-checked={value === undefined || value === null}
          aria-label={`${label} none`}
          title="none"
          onClick={() => onChange(undefined)}
          className="flex size-7 items-center justify-center rounded border border-dashed text-[10px] text-muted-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          ⃠
        </button>
      ) : null}
      {Object.keys(theme[kind]).map((key) => (
        <button
          key={key}
          type="button"
          role="radio"
          aria-checked={value === key}
          aria-label={`${label} ${key}`}
          title={`${key} preview`}
          onClick={() => onChange(key)}
          className={cn(
            'flex size-7 items-center justify-center rounded border outline-none focus-visible:ring-2 focus-visible:ring-ring',
            value === key && 'ring-2 ring-indigo-500'
          )}
        >
          <ThemeTokenSample family={kind} token={key} theme={theme} />
        </button>
      ))}
    </div>
  );
}

export function TokenGrid({
  label,
  kind,
  theme,
  value,
  onChange,
  optional,
  onMint,
}: {
  label: string;
  kind: 'colors' | 'fonts' | 'buttons';
  theme: Theme;
  value: string | null | undefined;
  onChange: (value: string | undefined) => void;
  /** May the token be ABSENT? (`bg` may; a `text`'s `color` may not.) */
  optional?: boolean;
  /** Mint a NEW colour and assign it, in the caller's one `applyChange`. Colours only — see
   *  `TokenPicker.onMint` for why the other families get extract-on-demand instead. */
  onMint?: ((light: string, dark: string) => void) | undefined;
}): React.JSX.Element {
  return (
    <div
      data-token-control=""
      className="grid w-full min-w-0 grid-cols-[minmax(0,1fr)_32px_32px] items-center gap-1"
    >
      <Popover
        label={label}
        className="w-full"
        trigger={
          <>
            {value ? <ThemeTokenSample family={kind} token={value} theme={theme} compact /> : null}
            <span className="truncate text-[12px]">{value ?? 'none'}</span>
          </>
        }
      >
        <TokenSwatchGrid label={label} kind={kind} theme={theme} value={value} onChange={onChange} optional={optional} />
      </Popover>
      <div data-token-action="mint" className="grid size-8 place-items-center">
        {onMint ? <ColorMintPopover theme={theme} onMint={onMint} /> : <span aria-hidden="true" className="size-8" />}
      </div>
      <div data-token-action="theme" className="grid size-8 place-items-center">
        {value ? <OpenThemeTokenLink family={kind} token={value} /> : <span aria-hidden="true" className="size-8" />}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------- angle dial

/** Paired with a number input, never alone: a dial is fast to aim and hopeless to be exact with,
 *  and a gradient angle is a value people copy between documents. */
export function AngleDial({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
}): React.JSX.Element {
  const radians = ((value - 90) * Math.PI) / 180;
  const aim = (e: React.PointerEvent<SVGSVGElement>): void => {
    const box = e.currentTarget.getBoundingClientRect();
    const dx = e.clientX - (box.left + box.width / 2);
    const dy = e.clientY - (box.top + box.height / 2);
    const deg = Math.round((Math.atan2(dy, dx) * 180) / Math.PI + 90 + 360) % 360;
    onChange(deg);
  };

  return (
    <>
      <svg
        viewBox="0 0 28 28"
        className="size-7 shrink-0 cursor-grab touch-none"
        onPointerDown={(e) => {
          e.currentTarget.setPointerCapture(e.pointerId);
          aim(e);
        }}
        onPointerMove={(e) => {
          if (e.currentTarget.hasPointerCapture(e.pointerId)) aim(e);
        }}
      >
        <circle cx="14" cy="14" r="12" className="fill-transparent stroke-border" />
        <line
          x1="14"
          y1="14"
          x2={14 + Math.cos(radians) * 10}
          y2={14 + Math.sin(radians) * 10}
          className="stroke-indigo-400"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
      <input
        type="number"
        aria-label={label}
        min={0}
        max={360}
        value={value}
        onChange={(e) => onChange(Math.min(360, Math.max(0, Number(e.target.value) || 0)))}
        className={cn(CONTROL, 'w-[56px]')}
      />
    </>
  );
}
